import { defineStore } from "pinia";
import { ref } from "vue";
import { accessGrantRepository } from "../repositories";
export const useAccessGrantStore = defineStore("access-grants", () => {
    // ---------------------------------------------------------------------------
    // STATE
    // ---------------------------------------------------------------------------
    const grants = ref([]);
    // The signed token is only ever present in the creation response. Held here
    // just long enough for the UI to show a one-time "copy this now" banner —
    // never persisted, never refetched.
    const newlyIssuedGrant = ref(null);
    const isLoading = ref(false);
    const error = ref(null);
    // ---------------------------------------------------------------------------
    // ACTIONS
    // ---------------------------------------------------------------------------
    async function load(orgId) {
        isLoading.value = true;
        error.value = null;
        try {
            grants.value = await accessGrantRepository.list(orgId);
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "Failed to load access grants";
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function issue(orgId, allowedOrigins, captchaRequired) {
        isLoading.value = true;
        error.value = null;
        try {
            const created = await accessGrantRepository.create(orgId, allowedOrigins, captchaRequired);
            newlyIssuedGrant.value = created;
            // The creation response omits lifecycle metadata (status/createdAt/
            // revokedAt), so refetch the authoritative list rather than splicing
            // the partial response in directly.
            await load(orgId);
            return created;
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "Failed to issue access grant";
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function revoke(orgId, grantId) {
        isLoading.value = true;
        error.value = null;
        try {
            await accessGrantRepository.revoke(orgId, grantId);
            // The revoke endpoint returns 204 with no body, and the live list/get
            // API doesn't reliably populate status, so a reload can't be trusted to
            // reflect this either way. The 204 itself is the confirmation — apply
            // it locally rather than trusting a refetch to show it.
            grants.value = grants.value.map((grant) => grant.id === grantId
                ? { ...grant, status: "revoked", revokedAt: new Date().toISOString() }
                : grant);
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : "Failed to revoke access grant";
            throw err;
        }
        finally {
            isLoading.value = false;
        }
    }
    function acknowledgeNewlyIssuedGrant() {
        newlyIssuedGrant.value = null;
    }
    function $reset() {
        grants.value = [];
        newlyIssuedGrant.value = null;
        isLoading.value = false;
        error.value = null;
    }
    // ---------------------------------------------------------------------------
    // RETURN
    // ---------------------------------------------------------------------------
    return {
        grants,
        newlyIssuedGrant,
        isLoading,
        error,
        load,
        issue,
        revoke,
        acknowledgeNewlyIssuedGrant,
        $reset,
    };
});
